import { useCallback, useEffect, useState } from 'react';
import api from '../../api/client.js';
import ConfirmDialog from '../../components/ConfirmDialog.jsx';

/**
 * Display links for the governor's screen.
 *
 * The previous system used a `?role=view` URL that only hid buttons — the server
 * still accepted writes from it. A link here opens a genuine read-only session
 * bound to a viewer account, and revoking it signs the screen out immediately.
 */
export default function DisplayLinksPanel({ showToast }) {
  const [tokens, setTokens] = useState([]);
  const [viewers, setViewers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [label, setLabel] = useState('');
  const [userId, setUserId] = useState('');
  const [creating, setCreating] = useState(false);
  const [pendingRevoke, setPendingRevoke] = useState(null);

  const load = useCallback(async () => {
    try {
      const [tokenData, userData] = await Promise.all([
        api.getDisplayTokens(),
        api.getUsers(),
      ]);
      setTokens(tokenData.tokens);

      const viewerAccounts = userData.users.filter(
        (user) => user.role === 'viewer' && user.isActive
      );
      setViewers(viewerAccounts);
      setUserId((current) => current || String(viewerAccounts[0]?.id || ''));
    } catch (caught) {
      showToast(caught?.message || 'تعذّر تحميل روابط الشاشات', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleCreate(event) {
    event.preventDefault();
    if (creating || !userId) return;

    setCreating(true);
    try {
      await api.createDisplayToken(label, Number(userId));
      setLabel('');
      await load();
      showToast('تم إنشاء رابط الشاشة');
    } catch (caught) {
      showToast(caught?.message || 'تعذّر إنشاء الرابط', 'error');
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke() {
    try {
      await api.revokeDisplayToken(pendingRevoke.token);
      setPendingRevoke(null);
      await load();
      showToast('تم إلغاء الرابط وإنهاء جلسة الشاشة');
    } catch (caught) {
      showToast(caught?.message || 'تعذّر إلغاء الرابط', 'error');
    }
  }

  function linkFor(token) {
    return `${window.location.origin}/display?token=${token}`;
  }

  async function copyLink(token) {
    try {
      await navigator.clipboard.writeText(linkFor(token));
      showToast('تم نسخ الرابط');
    } catch {
      // Clipboard access needs a secure context; the link is displayed in full
      // beside the button so it can be selected manually.
      showToast('تعذّر النسخ التلقائي. يمكنك تحديد الرابط ونسخه يدوياً.', 'error');
    }
  }

  return (
    <section className="panel">
      <div className="panel-header">
        <div>
          <h2>روابط شاشات العرض</h2>
          <p>
            رابط دائم يفتح جدول اليوم للقراءة والطباعة فقط، دون إدخال كلمة مرور — لشاشة
            مكتب السيد المحافظ. الصلاحية محدودة على الخادم، وإلغاء الرابط ينهي جلسة الشاشة
            فوراً.
          </p>
        </div>
      </div>

      <div className="panel-body">
        {viewers.length === 0 ? (
          <div className="empty-state">
            <h3>لا يوجد حساب «عرض فقط»</h3>
            <p>أنشئ حساباً بدور «عرض فقط» من تبويب الحسابات أولاً، ثم أنشئ رابط الشاشة.</p>
          </div>
        ) : (
          <form onSubmit={handleCreate} style={{ marginBottom: '24px' }}>
            <div className="field-row">
              <div className="field">
                <label htmlFor="token-label">اسم الشاشة</label>
                <input
                  id="token-label"
                  value={label}
                  onChange={(event) => setLabel(event.target.value)}
                  placeholder="مثال: شاشة مكتب المحافظ"
                  required
                />
              </div>
              <div className="field">
                <label htmlFor="token-user">الحساب المرتبط</label>
                <select
                  id="token-user"
                  value={userId}
                  onChange={(event) => setUserId(event.target.value)}
                >
                  {viewers.map((viewer) => (
                    <option key={viewer.id} value={viewer.id}>
                      {viewer.displayName}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <button type="submit" className="btn btn-gold" disabled={creating}>
              {creating ? '...جاري الإنشاء' : 'إنشاء رابط'}
            </button>
          </form>
        )}

        {loading ? (
          <div className="loading-state">...جاري التحميل</div>
        ) : tokens.length === 0 ? (
          <p className="muted">لم يتم إنشاء أي رابط بعد.</p>
        ) : (
          <div className="stack">
            {tokens.map((token) => (
              <div
                key={token.token}
                style={{
                  border: '1px solid var(--line)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '12px',
                }}
              >
                <div className="row">
                  <strong>{token.label}</strong>
                  <span className="muted">— {token.userDisplayName}</span>
                  <span className="spacer" />
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={() => copyLink(token.token)}
                  >
                    نسخ الرابط
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-danger"
                    onClick={() => setPendingRevoke(token)}
                  >
                    إلغاء
                  </button>
                </div>

                <div className="token-link" style={{ marginTop: '8px' }}>
                  {linkFor(token.token)}
                </div>

                <p className="hint">
                  {token.lastUsedAt
                    ? `آخر استخدام: ${new Date(token.lastUsedAt).toLocaleString('ar-SY')}`
                    : 'لم يُستخدم بعد'}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      {pendingRevoke && (
        <ConfirmDialog
          title="إلغاء رابط الشاشة"
          message={`هل تريد إلغاء رابط «${pendingRevoke.label}»؟`}
          detail="ستتوقف أي شاشة تستخدم هذا الرابط فوراً، وسيلزم إنشاء رابط جديد وفتحه على تلك الشاشة."
          confirmLabel="إلغاء الرابط"
          onConfirm={handleRevoke}
          onCancel={() => setPendingRevoke(null)}
        />
      )}
    </section>
  );
}
